import React, { useState, useEffect } from 'react';

import { useSasToken } from '../../datahooks/admin/imageUploadHooks';

import { Avatar, Dialog, DialogBody, DialogFooter} from '@material-tailwind/react';

import { IMAGES_URL, PRIVATE_IMAGES } from '../../config';
import { makeAbbreviation } from '@datahooks/metadata/DataUtils';

import PropTypes from 'prop-types';
import { TestableButton, TestableText } from './TestableComponents';

/**
 * @typedef {Object} AvatarLogoProps
 * @property {string} [src] The source URL of the image, relative to the image base URL.
 * @property {string} [alt] The alternative/tooltip text for the image.
 * @property {'xs'|'sm'|'md'|'lg'|'xl'|'xxl'|'full'} [size='md'] The size of the avatar/logo.
 * @property {number} [initialsCount=1] Number of initials to display in the placeholder.
 * @property {'avatar'|'logo'} [displayType='avatar'] Render mode.
 * @property {boolean} [withBorder=false] Whether to display a border around the avatar.
 * @property {boolean} [expandable=false] Whether the logo can be expanded to a larger view on click.
 */

/**
 * Renders an avatar/logo image with fallback initials.
 *
 * @param {AvatarLogoProps} props
 * @returns {JSX.Element|null}
 */
const AvatarLogo = ({ src, alt, size = 'md', initialsCount = 2, displayType = 'avatar', withBorder = false, expandable=false }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [hasError, setHasError] = useState(false);
  const { sasToken, isLoading: isTokenLoading, error: isTokenError, triggerFetchToken } = useSasToken('read');
  const [ isDialogOpen, setIsDialogOpen] = useState(false);

  const toggleDialog = () => setIsDialogOpen(!isDialogOpen);

  useEffect(() => {
    const fetchImageUrl = async () => {
      try {
        if (!src) return;
        // The source is a blob when we've selected an image from the file input and are showing a preview
        // If the source starts /, it is a local asset from the project source
        const isBlobUrl = src?.startsWith('/') || src?.startsWith('blob:');

        if (isBlobUrl) {
          setImageUrl(src);
          return;
        }

        const isFullUrl = src.startsWith('http');
        const formattedSrc = src.startsWith('/') ? src.slice(1) : src;
        const finalSrc = isFullUrl ? src : `${IMAGES_URL}${formattedSrc}`;
       
        if (PRIVATE_IMAGES) {   //Storage container is set to private so we need to fetch a SAS token

          if (!sasToken) {  //Get a SAS token if one is not already available in cache
            if (!isTokenLoading && !isTokenError) {
              triggerFetchToken();
            }
            return; // Wait for token to load before setting the image URL
          }
          const concatCharacter = finalSrc.includes('?') ? '&' : '?';
          setImageUrl(`${finalSrc}${concatCharacter}${sasToken}`);
        } else {    //Storage container is public so we can use the regular URL
          setImageUrl(finalSrc);
        }
      } catch (err) {
        console.error('Error fetching image URL:', err);
        setHasError(true);
      }
    };

    // Prevent stale caching by clearing the image URL before attempting to load
    setImageUrl('');
    setHasError(false);

    if (src && src !== 'null') {
      fetchImageUrl();
    }
  }, [src, sasToken]);

  // Set the placeholder text
  const placeholder = alt ? makeAbbreviation(alt, initialsCount) : 'X';
  const shouldDisplayPlaceholder = hasError || !imageUrl;

  const avatarSizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-14 h-14 text-xl',
    xxl: 'w-16 h-16 text-2xl',
    full: 'w-auto h-auto',
  };

  const imageSizeClasses = {
    xs: 'h-8 text-xs',
    sm: 'h-12 text-sm',
    md: 'h-14 text-base',
    lg: 'h-16 text-lg',
    xl: 'h-20 text-xl',
    xxl: 'h-24 text-2xl',
    full: 'h-auto w-full',
  };

  const avatarAppliedSizeClass = avatarSizeClasses[size] || avatarSizeClasses.md;
  const appliedSizeClass = imageSizeClasses[size] || imageSizeClasses.md;

  const handleClick = () => {
    if (expandable) {
      toggleDialog();
    }
  };

  if (shouldDisplayPlaceholder) {
    return (
      <div data-testid="avatarlogo-placeholder" className={`flex flex-shrink-0 justify-center items-center rounded-full bg-gray-600 text-white ${avatarAppliedSizeClass}`}>
        {placeholder}
      </div>
    );
  }

  if (displayType === "avatar") {
    return (
      <Avatar
        data-testid="avatarlogo-avatar-image"
        src={imageUrl}
        alt={alt}
        size={size}
        onError={() => setHasError(true)}
        withBorder={withBorder}
        color="blue"
        className="p-0.5"
      />
    );
  }

  const logoImage = (
    <img  
      data-testid="avatarlogo-logo-image" 
      src={imageUrl} onError={() => setHasError(true)} 
      alt={alt} 
      className={`object-contain ${appliedSizeClass}`} />
  );

  return expandable ? (
    <>
      <TestableButton dataTestId="avatarlogo-logo-button" onClick={() => handleClick()} className="p-0 m-0 bg-transparent w-full">
        {logoImage}
        {alt && (
          <TestableText dataTestId='avatarlogo-logo-button-text' className="text-center normal-case text-sm mt-1 text-blue-500">{alt}</TestableText>
        )}
      </TestableButton>

      <Dialog data-testid='avatarlogo-dialog' open={isDialogOpen} handler={toggleDialog} size="xl">
        <DialogBody data-testid='ald-body' className="flex justify-center items-center">
          <img data-testid="aldb-image-expanded" src={imageUrl} onError={() => setHasError(true)} alt={alt} className={`object-contain max-h-[70vh] max-w-full`} />
        </DialogBody>
        {alt && (
          <DialogFooter data-testid='ald-footer' className='border-t border-gray-300 mx-4'>
            <TestableText dataTestId='aldf-label' className="w-full text-center">{alt}</TestableText>
          </DialogFooter>
        )}
      </Dialog>
    </>
  ) : (
    logoImage
  );
};

AvatarLogo.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl','full']),
  initialsCount: PropTypes.number,
  displayType: PropTypes.oneOf(['avatar', 'logo']),
  withBorder: PropTypes.bool,
  expandable: PropTypes.bool
};

export default AvatarLogo;